import { TaskAdmin } from '@gleamer/types';
import classNames from 'classnames';
import React from 'react';
import { PageButton } from '../../../atoms/PageButton';
import {
  COMPUTATION_STATUS_LABELS,
  COMPUTATION_STATUS_MEANINGS,
  IMPORT_STATUS_LABELS,
  IMPORT_STATUS_MEANINGS,
} from './activity';

type ComputationStatus = TaskAdmin.ComputationStatus;
type IssueSeverity = TaskAdmin.IssueSeverity;
type PredictionImportStatus = TaskAdmin.PredictionImportStatus;

function Badge({
  className,
  title,
  children,
}: React.PropsWithChildren<{ className?: string; title?: string }>) {
  return (
    <span
      title={title}
      className={classNames(
        'inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-bold',
        className ?? 'bg-dl-track text-dl-text'
      )}
    >
      {children}
    </span>
  );
}

const COMPUTATION_STYLES: Record<ComputationStatus, string> = {
  RUNNING: 'bg-dl-tint text-dl-accent',
  DONE: 'bg-dl-success-tint text-dl-success',
  DONE_WITH_ISSUES: 'bg-dl-warning-tint text-dl-warning',
  FAILED: 'bg-dl-danger-tint text-dl-danger',
  SUPERSEDED: 'bg-dl-track text-dl-text',
};

export function ComputationStatusBadge({
  status,
}: {
  status: ComputationStatus;
}) {
  return (
    <Badge
      className={COMPUTATION_STYLES[status]}
      title={COMPUTATION_STATUS_MEANINGS[status]}
    >
      {COMPUTATION_STATUS_LABELS[status]}
    </Badge>
  );
}

const IMPORT_STYLES: Record<PredictionImportStatus, string> = {
  WAITING: 'bg-dl-track text-dl-muted',
  REQUESTED: 'bg-dl-track text-dl-text',
  IMPORTING: 'bg-dl-tint text-dl-accent',
  DONE: 'bg-dl-success-tint text-dl-success',
  PARTIAL_FAILURE: 'bg-dl-warning-tint text-dl-warning',
  FAILED: 'bg-dl-danger-tint text-dl-danger',
};

export function ImportStatusBadge({
  status,
}: {
  status: PredictionImportStatus;
}) {
  return (
    <Badge
      className={IMPORT_STYLES[status]}
      title={IMPORT_STATUS_MEANINGS[status]}
    >
      {IMPORT_STATUS_LABELS[status]}
    </Badge>
  );
}

const SEVERITY_STYLES: Record<IssueSeverity, string> = {
  WARNING: 'bg-dl-warning-tint text-dl-warning',
  ERROR: 'bg-dl-danger-tint text-dl-danger',
};

export function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  return <Badge className={SEVERITY_STYLES[severity]}>{severity}</Badge>;
}

/** Figures side by side: a label above its number, `—` for one not known yet. */
export function Counts({
  counts,
}: {
  counts: { label: string; value: number | null | undefined; tone?: string }[];
}) {
  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(6.5rem,1fr))] gap-2">
      {counts.map(({ label, value, tone }) => (
        <div
          key={label}
          className="rounded-lg bg-dl-sunken px-3 py-2"
        >
          <dt className="text-xs font-medium text-dl-muted">{label}</dt>
          <dd
            className={classNames(
              'text-lg font-semibold tabular-nums',
              value ? (tone ?? 'text-dl-text') : 'text-dl-text'
            )}
          >
            {value === null || value === undefined
              ? '—'
              : value.toLocaleString()}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/** Previous and next over `total` rows, `limit` at a time, from `offset`. */
export function Pager({
  label,
  total,
  limit,
  offset,
  onOffset,
}: {
  label: string;
  total: number;
  limit: number;
  offset: number;
  onOffset: (offset: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / limit));
  const page = Math.floor(offset / limit);
  return (
    <div className="flex flex-wrap items-center gap-1 py-2 text-sm">
      <span className="mr-1 text-dl-muted">
        Page {page + 1} of {pages} ({total.toLocaleString()} {label})
      </span>
      {pages > 1 && (
        <>
          <PageButton
            className="px-2 py-0.5 text-sm"
            onClick={() => onOffset(Math.max(0, offset - limit))}
            disabled={page === 0}
          >
            Previous page
          </PageButton>
          <PageButton
            className="px-2 py-0.5 text-sm"
            onClick={() => onOffset(offset + limit)}
            disabled={page + 1 >= pages}
          >
            Next page
          </PageButton>
        </>
      )}
    </div>
  );
}
